<!-- CommentWidget.vue -->
<!-- Lightweight comment input that creates a Work record of type "comment".
     Two placements:
       1. Quality_Gap_Detail — with opportunityId (links to care gap)
       2. Patient_Engagement — without opportunityId (patient-level comment) -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 px-4 py-3">
    <div class="flex items-start gap-2.5">
      <textarea
        v-model="commentText"
        :disabled="submitting"
        rows="2"
        class="flex-1 text-xs border border-gray-200 rounded px-2.5 py-1.5 resize-none
               focus:ring-1 focus:ring-primary-500 focus:border-primary-500
               placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
        placeholder="Add a comment..."
        @keydown.meta.enter="submitComment"
        @keydown.ctrl.enter="submitComment"
      ></textarea>
      <button
        :disabled="!commentText.trim() || submitting || !commentWorkTypeId"
        class="px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 rounded
               hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed
               transition-colors shrink-0"
        @click="submitComment"
      >
        <FontAwesomeIcon v-if="submitting" :icon="['fas', 'spinner']" spin class="mr-1" />
        Comment
      </button>
    </div>
    <p v-if="error" class="mt-2 text-xs text-red-600">{{ error }}</p>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'CommentWidget',
  components: { FontAwesomeIcon },

  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, required: true },
    opportunityId: { type: Number, default: null }
  },

  emits: ['comment-saved'],

  data() {
    return {
      commentText: '',
      submitting: false,
      error: null,
      commentWorkTypeId: null
    };
  },

  computed: {
    ...mapGetters('auth', ['current_user'])
  },

  async mounted() {
    await this.resolveCommentWorkType();
  },

  methods: {
    async resolveCommentWorkType() {
      try {
        const { data } = await axios.get('/api/worktypes/');
        const existing = data.find(wt => wt.name === 'comment');
        if (existing) {
          this.commentWorkTypeId = existing.id;
          return;
        }
        // Auto-provision if missing
        const { data: created } = await axios.post('/api/worktypes/', {
          name: 'comment',
          display_name: 'Comment'
        });
        this.commentWorkTypeId = created.id;
      } catch (err) {
        console.error('Failed to resolve comment work type:', err);
        this.error = 'Unable to load comment work type';
      }
    },

    async submitComment() {
      const text = this.commentText.trim();
      if (!text || !this.commentWorkTypeId) return;

      this.submitting = true;
      this.error = null;

      try {
        const payload = {
          work_type: this.commentWorkTypeId,
          appuser: this.current_user.id,
          entity: this.entityId,
          metadata: {
            notes: text,
            patient_identifier: this.patientIdentifier,
            timestamp: new Date().toISOString()
          }
        };

        if (this.opportunityId) {
          payload.opportunity_ids = [this.opportunityId];
          payload.opportunity = this.opportunityId;
        }

        await axios.post('/api/work/', payload);
        this.commentText = '';
        this.$emit('comment-saved');
      } catch (err) {
        console.error('Failed to save comment:', err);
        this.error = 'Failed to save comment. Please try again.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
